import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { AuthService } from 'services/auth.service';
import { CouchLoginReason } from 'services/couch-login-storm';
import { CoachPresenceService } from 'services/coach-presence.service';
import { DatabaseWrapper, DocWatcher } from 'services/database-wrapper';
import { DebugService } from 'services/debug.service';
import {
  clearEquipmentForUnit, clearLegacyEquipmentId, equipmentUnitOrKg, forgetAllEquipmentOnStat, forgetEquipmentOnStat, legacyEquipmentId,
} from 'services/exercise-stat-equipment';
import { ExerciseDbService } from 'services/exercise-db.service';
import { RestTimerService } from 'services/rest-timer.service';
import { SettingsService } from 'services/settings.service';
import { UserDb } from 'services/user-db.service';
import { accountConsentsChangedEvent, coachBannerEvent, PubSubService } from 'services/pubsub.service';
import { CONSENT_TEXT_VERSION } from '@cf-shared/consent-refresh';
import { UserConsentId } from '@cf-shared/user-consent-interface';
import { setSmallScreenMaxSize, smallScreenMaxWidthBase } from 'environments/environment-helpers';
import { findControlsUnderBottomInset } from 'services/safe-area-overlap-monitor.service';
import { deepClone } from '@cf-shared/deep-helpers';
import { Equipment } from '@cf-shared/data-model/equipment';
import { ExerciseStat } from '@cf-shared/data-model/user-state';
import { isCustomExerciseById } from '@cf-shared/data-model/exercise';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { getEntryLocalTimeInDesiredTimeZone } from '@cf-shared/data-model/user-log';
import { Log_Meal, pickOneMealPerSlot } from '@cf-shared/data-model/log-meal';

/**
 * The few things these hooks need that belong to `AppComponent` and cannot move
 * with them. `AppComponent` passes this in when it registers.
 */
export interface E2eTestHooksHost {
  /**
   * Read as a function on purpose, NOT as a value. The boot doc watcher does not
   * exist yet when registration runs — the boot sequence creates it later — so a
   * captured value would be `null` for the whole life of the app.
   */
  getDocWatcher(): DocWatcher;
  /** Also driven by the dev-only "next day" button in the app template, so it stays on the component. */
  fakeNextDayAsync(): Promise<any>;
  /** The component's own change detector, for the hooks that change what is on screen. */
  markForCheck(): void;
}

/**
 * The `window['…']` hooks that let E2E specs and a DevTools console reach into a
 * running app (second slice of deferred finding df-7b66acbeb341).
 *
 * None of this is application behavior. Nothing a customer does reaches any of
 * it: every hook runs only when something outside the app calls it by name.
 * E2E specs call them through `E2E.asyncExec`, which evaluates a string in the
 * page, so the NAMES below are the contract — renaming one breaks the spec that
 * calls it, and nothing in a compile would say so.
 *
 * These deliberately ship on deployed dev, test and production builds rather
 * than being gated to local dev. The reason is recorded on the `window['userDb']`
 * assignment below and predates this move.
 *
 * Lifted out of `AppComponent`'s constructor with no behavior change. That
 * constructor still calls `register()` at exactly the point the block used to
 * sit, because the hooks must exist before any spec can call them — see the
 * comment on `register()`.
 */
@Injectable({ providedIn: 'root' })
export class E2eTestHooksService {
  constructor(
    private auth: AuthService,
    private userDb: UserDb,
    private debug: DebugService,
    private exerciseDb: ExerciseDbService,
    private settings: SettingsService,
    private pubSub: PubSubService,
    private coachPresence: CoachPresenceService,
    private restTimer: RestTimerService,
  ) { }

  /**
   * Install every hook on `window`.
   *
   * MUST be called from `AppComponent`'s constructor, and nothing else may be
   * relied on to trigger it. An Angular service is built on first injection, so
   * a hooks service nobody injects early would simply never register — and the
   * symptom would be 16 E2E spec files failing with "not a function", pointing
   * at the specs rather than at the cause.
   */
  register(host: E2eTestHooksHost): void {
    window['refreshCouchSessionAsync'] = async () => {
      return this.auth.attemptCouchLoginAsync(CouchLoginReason.E2eTestHook);
    };
    // expose userDb / auth singletons for ad-hoc DevTools queries and E2E
    // precondition scripts — needed on deployed dev/test/prod builds, not just
    // local, because Selenium-grid runs against the deployed dev URL.
    // d15a70ec1 originally gated these behind !isAngularProduction citing
    // "raw service refs are a strict superset of the function hooks below for
    // a page-context attacker"; the peer hooks (fakeDateLocalISOAsync,
    // testCorruptNutritionLabelStyleAsync, testCoachPresenceStartHeartbeatAsync,
    // …) already ship unguarded and grant equivalent reach into the user DB,
    // so the gate carried no real isolation — just hid two refs while a dozen
    // doors stood open.
    window['userDb'] = this.userDb;
    window['auth'] = this.auth;
    window['fakeDateLocalISOAsync'] = async (d: string) => {
      return this.debug.setNowLocalDayAsync(DateTime.fromISO(d));
    };
    window['fakeNextDayISOAsync'] = async () => {
      return host.fakeNextDayAsync();
    };
    window['getDebugMealDataAsync'] = async () => {
      const now = this.debug.getNowLocal();
      const start = now.startOf('day');
      const end = now.endOf('day');
      const entries = await this.userDb.getLogEntriesInRangeAsync(start, end, host.getDocWatcher(), { inLogEntriesTimeZone: true });
      const mealsOnDay = entries.filter(e => e.type === 'Meal') as Log_Meal[];
      // collapse to one meal per slot, the same rule every screen that totals a day applies.
      // A day can legitimately hold two documents for one meal — a log entry holding food the
      // user typed in is never deleted — so returning both would report a day total nobody's
      // screen shows, and a caller summing these targets would over-count by a whole meal.
      return pickOneMealPerSlot(mealsOnDay, now).kept
        .map((m: any) => ({
          name: m.name,
          cutMeal: m.cutMeal,
          targetCalories: m.targets?.['ENER$_kcal']?.target,
          planningTargetCalories: m.planningTargets?.['ENER$_kcal']?.target,
          loggedCalories: m.nutrients?.['ENER$_kcal'],
        }));
    };
    // #4511 — the day card's calorie target and the meal cards' targets are built from the
    // same plan in one pass, so they cannot legitimately disagree. When the E2E sees them
    // disagree it asks for this, to say WHICH wake-up entry the day is holding: one that
    // reverted to an older plan, or two documents with different targets. Reads the cache
    // the screens themselves read, not the disk, because that is where the wrong number was.
    window['getDebugDayStartDataAsync'] = async () => {
      const today = this.debug.getNowLocal().startOf('day');
      return this.userDb.backlogCache.getCachedEntriesSnapshot()
        .filter((e: any) => e.type === 'DayStart'
          && getEntryLocalTimeInDesiredTimeZone(e, today).toISODate() === today.toISODate())
        .map((e: any) => ({
          _id: e._id,
          _rev: e._rev,
          state: e.state,
          time_utc: e.time_utc,
          timeOffset_min: e.timeOffset_min,
          targetCalories: e.targets?.['ENER$_kcal']?.target,
        }));
    };
    // Simulate the corrupted PouchDB state that triggers the settings race condition (#2701):
    // nutritionLabelStyle=undefined in PouchDB while localStorage has a valid value.
    window['testCorruptNutritionLabelStyleAsync'] = async () => {
      this.userDb.settings.nutritionLabelStyle = undefined;
      await this.userDb.saveSettingsAsync(DatabaseWrapper.getTransactionId());
    };

    // Coach-presence test hooks: drive the service directly from E2E tests.
    // Kept minimal — E2E exercises the write/read path, not the visual icon.
    window['testCoachPresenceStartHeartbeatAsync'] = async (docId: string, displayName: string) => {
      this.coachPresence.startHeartbeat(docId, displayName);
    };
    window['testCoachPresenceStopHeartbeatAsync'] = async (docId: string) => {
      this.coachPresence.stopHeartbeat(docId);
    };
    window['testCoachPresenceReadLocalAsync'] = async (docId: string) => {
      try {
        return await this.userDb.database.withDocWatcherAsync(watcher => this.userDb.database.getRecordAsync(docId, watcher));
      } catch (err: any) {
        return { error: err?.name || err?.message || String(err) };
      }
    };
    window['testCoachPresenceGetTimeSinceString'] = (lastSeenUtc: string) => {
      return this.coachPresence.getTimeSinceString(lastSeenUtc);
    };

    // Coach-dashboard pure-helper hooks — smoke-test the bundled helpers from E2E
    // (see `design-docs/coach-mode.md` → Coach Dashboard). Heavier dashboard
    // behaviors are covered by the unit-test suite + the manual STOP verification
    // step in `plans/coach-dashboard.md`.
    window['testCoachDashboardHelpers'] = async (input: {
      solverResult?: any;
      logs?: any[];
      nowUtc?: string;
      windowDays?: number;
      rows?: any[];
    }) => {
      const { deriveClientStatus, detectActionItems, sortDashboardRows, DEFAULT_ACTION_ITEM_WINDOW_DAYS } = await import('services/coach-dashboard-helpers');
      const { DateTime } = await import('luxon');
      const out: any = {};
      if (input.solverResult) {
        out.status = deriveClientStatus(input.solverResult);
      }
      if (input.logs) {
        const now = input.nowUtc ? DateTime.fromISO(input.nowUtc, { zone: 'utc' }) : DateTime.utc();
        out.items = detectActionItems(input.logs, now, input.windowDays ?? DEFAULT_ACTION_ITEM_WINDOW_DAYS);
      }
      if (input.rows) {
        out.sorted = sortDashboardRows(input.rows);
      }
      return out;
    };

    // seed a Log_WeeklyCheckup doc in the AwaitingCoach state for split-checkup E2E coverage.
    // returns the new doc's _id so the spec can read it back and verify transitions.
    window['testSeedAwaitingCoachCheckupAsync'] = async (opts?: { recommendedAvgCalories?: number; calorieIntakeBaseline?: number }) => {
      const { createWeeklyCheckupEntry, WeeklyCheckupCompletionAction, CalorieIntakeBaseline } = await import('@cf-shared/data-model/log-weekly-checkup');
      const { LogRecordState } = await import('@cf-shared/data-model/user-log');
      const entry = createWeeklyCheckupEntry(this.debug.getNowLocal());
      // submitted checkup is Done from the client's perspective — coach resolves it in place
      entry.state = LogRecordState.Done;
      entry.completionAction = WeeklyCheckupCompletionAction.AwaitingCoach;
      entry.selectedCalorieIntakeBaseline = CalorieIntakeBaseline.Target;
      entry.calorieIntakeBaseline = opts?.calorieIntakeBaseline ?? 2000;
      entry.recommendedAvgCalories = opts?.recommendedAvgCalories ?? 2200;
      entry.isWeightGainGoal = true;
      // minimal pending payload — enough to round-trip through getPendingPlan
      entry.pendingNewPlan = JSON.stringify({ actualDailyAverageIntake: entry.recommendedAvgCalories, nominalDailyAverageIntake: entry.recommendedAvgCalories });
      entry.pendingNewFitnessState = JSON.stringify({ bfData: { bf_pc: 15 } });
      await this.userDb.saveEntryAsync(DatabaseWrapper.getTransactionId(), entry);
      return entry._id;
    };

    // read back a checkup doc by id for verification
    window['testReadCheckupAsync'] = async (id: string) => {
      try {
        return await this.userDb.database.withDocWatcherAsync(watcher => this.userDb.getOneLogEntryAsync(id, watcher));
      } catch (err: any) {
        return { error: err?.name || err?.message || String(err) };
      }
    };

    // Corrupt intakeFormState to simulate stale incomplete session (#2956 repro):
    // clears all fields except time_utc so the solver crashes on missing data
    window['testCorruptIntakeFormStateAsync'] = async () => {
      const intakeFormState = await this.userDb.database.withDocWatcherAsync(watcher => this.userDb.loadIntakeFromStateAsync(watcher));
      const keysToKeep = new Set(['_id', '_rev', 'db__dirty', 'db__writable', 'time_utc']);
      for (const key of Object.keys(intakeFormState)) {
        if (!keysToKeep.has(key) && !key.startsWith('_')) {
          delete intakeFormState[key];
        }
      }
      await this.userDb.saveIntakeFromStateAsync(DatabaseWrapper.getTransactionId());
    };

    // Set training days in declared state WITHOUT updating schedule rules (#2778 repro)
    window['testSetTrainingDaysAsync'] = async (trainingDays: boolean[]) => {
      const declared = await this.userDb.database.withDocWatcherAsync(watcher => this.userDb.loadDeclaredStateAsync(watcher));
      declared.trainingDays = trainingDays;
      await this.userDb.saveDeclaredStateAsync(DatabaseWrapper.getTransactionId());
    };

    // Set combo set groups on a workout (#2961 repro)
    // groups: array of [subSlotIndex, groupNumber] pairs (e.g., [[0,1],[1,1]] = first two exercises in group 1)
    window['testSetComboSetGroupsAsync'] = async (workoutIndex: number, groups: [number, number][]) => {
      const plan = this.userDb.fitnessPlan;
      const workout = plan.workouts[workoutIndex];
      if (!workout) throw new Error(`No workout at index ${workoutIndex}`);
      const comboSetGroups: { [subSlotId: string]: number } = {};
      for (const [subSlotIdx, groupNum] of groups) {
        const subSlotId = workout.subSlotIds[subSlotIdx];
        if (!subSlotId) throw new Error(`No subSlot at index ${subSlotIdx}`);
        comboSetGroups[subSlotId] = groupNum;
      }
      workout.comboSetGroups = comboSetGroups;
      await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
    };

    // Fill the shared slot/$custom index pool up to its (pre-#3483) ceiling so the
    // next custom-exercise add hits the full-pool path. Clones an existing $custom
    // subslot (sourceSubSlotId, typically $custom$0) into indices 100..109 and
    // references them from the given workout so load-time orphan-GC keeps them.
    // (#3483 repro)
    window['testFillCustomSlotPoolAsync'] = async (workoutIndex: number, sourceSubSlotId: string) => {
      const plan = this.userDb.fitnessPlan;
      const workout = plan.workouts[workoutIndex];
      if (!workout) throw new Error(`No workout at index ${workoutIndex}`);
      const source = plan.subSlots[sourceSubSlotId];
      if (!source) throw new Error(`No subslot ${sourceSubSlotId}`);
      // for each index 100..109, clone the source subslot into slot/$custom$<idx>
      for (let idx = 100; idx <= 109; idx++) {
        const clone = deepClone(source);
        clone.slotId = 'slot/$custom';
        clone.startIndex = idx;
        clone.exercisePaths = [];
        const id = `$custom$${idx}`;
        plan.subSlots[id] = clone;
        if (!workout.subSlotIds.includes(id)) workout.subSlotIds.push(id);
      }
      await this.userDb.savePlanAsync(DatabaseWrapper.getTransactionId());
    };

    // wipe allEquipment + all exerciseStats.equipmentIdByUnit so next
    // workout-detail load re-runs preselect from scratch (#3162 repro)
    window['testClearEquipmentForPreselectReproAsync'] = async () => {
      await this.userDb.database.withDocWatcherAsync(async watcher => {
        const fs = await this.userDb.loadFitnessStateAsync(watcher);
        fs.allEquipment = {};
        // for each exercise stat, clear equipment pointers
        for (const stat of Object.values(fs.exerciseStats || {}) as ExerciseStat[]) {
          forgetAllEquipmentOnStat(stat);
        }
        await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
      });
    };

    // forget which machine one exercise uses in one weight unit (#5613 repro). a template
    // exercise gets BOTH unit slots filled when the workout page loads, so a unit switch in the
    // UI alone always finds a machine already recorded for the new unit. clearing the slot lets
    // the spec reach the page's later fallbacks: with `removeOwnedMachinesForUnit` false the
    // person still owns a machine in that unit made from the exercise's template; with it true
    // they own none, so the page has to copy the other unit's machine
    window['testForgetUnitEquipmentAsync'] = async (exerciseId: string, unit: 'kg' | 'lb', opts?: { removeOwnedMachinesForUnit?: boolean }) => {
      await this.userDb.database.withDocWatcherAsync(async watcher => {
        const fs = await this.userDb.loadFitnessStateAsync(watcher);
        const stat = fs.exerciseStats?.[exerciseId];
        // if the exercise was never trained there is nothing to forget, and the spec is wrong
        if (!stat) {
          throw new Error(`testForgetUnitEquipmentAsync: no exercise stat for ${exerciseId}`);
        }
        // for every exercise, drop any pointer to this machine so nothing is left dangling
        const forgetEveryPointerTo = (equipmentId: string) => {
          for (const other of Object.values(fs.exerciseStats || {}) as ExerciseStat[]) {
            forgetEquipmentOnStat(other, equipmentId);
          }
        };
        clearEquipmentForUnit(stat, unit);
        // the legacy single-machine field is read as "recorded for this unit" when its machine is in it
        const legacyId = legacyEquipmentId(stat);
        const legacyMachine = legacyId && fs.allEquipment?.[legacyId];
        if (legacyMachine && equipmentUnitOrKg(legacyMachine) === unit) {
          clearLegacyEquipmentId(stat);
        }

        if (opts?.removeOwnedMachinesForUnit) {
          const exercise = isCustomExerciseById(exerciseId)
            ? await this.userDb.getCustomExerciseByIdAsync(exerciseId)
            : await this.exerciseDb.getGlobalExerciseByIdAsync(exerciseId, watcher);
          const templateId = exercise?.equipmentTemplateId;
          if (!templateId) {
            throw new Error(`testForgetUnitEquipmentAsync: exercise ${exerciseId} has no equipment template`);
          }
          // for each owned machine in that unit made from the template, delete it and every pointer to it
          for (const [id, eq] of Object.entries(fs.allEquipment || {}) as [string, Equipment][]) {
            if (eq.basedOnTemplateId !== templateId || equipmentUnitOrKg(eq) !== unit) {
              continue;
            }
            delete fs.allEquipment[id];
            forgetEveryPointerTo(id);
          }
        }
        await this.userDb.saveFitnessStateAsync(DatabaseWrapper.getTransactionId());
      });
    };

    // return a flat summary of allEquipment + exerciseStats (with each
    // exercise's equipmentTemplateId resolved) for #3162 repro assertions
    window['testGetEquipmentPreselectStateAsync'] = async () => {
      return await this.userDb.database.withDocWatcherAsync(async watcher => {
        const fs = await this.userDb.loadFitnessStateAsync(watcher);
        const equipment: Array<{ id: string, name: string, type: string, weight_unit: string, basedOnTemplateId: string }> = [];
        // for each equipment, pick the fields the assertion needs
        for (const [id, eq] of Object.entries(fs.allEquipment || {}) as [string, Equipment][]) {
          equipment.push({
            id,
            name: eq.name,
            type: eq.type,
            weight_unit: eq.weight_unit,
            basedOnTemplateId: eq.basedOnTemplateId,
          });
        }
        const stats: Array<{ exerciseId: string, equipmentTemplateId: string, weight_unit: string, equipmentIdByUnit: { kg?: string, lb?: string } }> = [];
        // for each exercise stat, resolve its exercise's equipmentTemplateId
        for (const [exerciseId, stat] of Object.entries(fs.exerciseStats || {}) as [string, ExerciseStat][]) {
          let templateId: string | undefined;
          try {
            // if it's a custom exercise, go through userDb; otherwise global exercise db
            if (isCustomExerciseById(exerciseId)) {
              const ex = await this.userDb.getCustomExerciseByIdAsync(exerciseId);
              templateId = ex?.equipmentTemplateId;
            } else {
              const ex = await this.exerciseDb.getGlobalExerciseByIdAsync(exerciseId, watcher);
              templateId = ex?.equipmentTemplateId;
            }
          } catch (err) {
            // if the exercise doc was deleted or never synced, leave templateId undefined
            if (!isPouchNotFound(err)) {
              console.log('testGetEquipmentPreselectStateAsync: resolve failed for', exerciseId, err);
            }
          }
          stats.push({
            exerciseId,
            equipmentTemplateId: templateId,
            weight_unit: stat.weight_unit,
            equipmentIdByUnit: stat.equipmentIdByUnit || {},
          });
        }
        return { equipment, stats };
      });
    };

    // the share of bodyweight an exercise counts, in percent (#5617 repro): a spec can then pick the
    // bodyweight that gives the load it needs without depending on the catalog's number
    window['testGetExerciseBwPcAsync'] = async (exerciseId: string) => {
      const exercise = isCustomExerciseById(exerciseId)
        ? await this.userDb.getCustomExerciseByIdAsync(exerciseId)
        : await this.userDb.database.withDocWatcherAsync(watcher => this.exerciseDb.getGlobalExerciseByIdAsync(exerciseId, watcher));
      if (!exercise) throw new Error(`testGetExerciseBwPcAsync: exercise ${exerciseId} was not found`);
      return exercise.bw_pc || 0;
    };

    // read fitnessState.weight_kg directly from the singleton (#3286 repro)
    window['testGetFitnessStateWeightAsync'] = async () => {
      return await this.userDb.database.withDocWatcherAsync(async watcher => {
        const fs = await this.userDb.loadFitnessStateAsync(watcher);
        return fs.weight_kg;
      });
    };

    // toggle the test-mode flag that lets the rest-timer UI (pill + expanded
    // overlay) run on PWA/desktop so it can be exercised in E2E without
    // mocking Capacitor. native plugins stay gated to isCapacitorNative().
    window['testSetDebugRestTimerOnWebAsync'] = async (enabled: boolean) => {
      this.settings.debugRestTimerOnWeb = enabled;
    };

    window['testSetForcedSmallScreenAsync'] = async (enabled: boolean) => {
      setSmallScreenMaxSize(enabled ? 99999 : smallScreenMaxWidthBase);
      window.dispatchEvent(new Event('resize'));
      host.markForCheck();
    };

    // the tappable controls stuck under the bottom safe-area inset, by the same rules the native
    // app reports to Sentry (#5810); E2E injects --safe-area-inset-bottom first, since a desktop
    // browser has no inset of its own
    window['testFindControlsUnderBottomInset'] = () => findControlsUnderBottomInset(document);

    // collapse a running rest timer to zero on the next tick so E2E can
    // exercise the expiry flow without waiting REST_TIMER_MIN_SECONDS.
    window['testForceRestTimerExpireAsync'] = async () => {
      this.restTimer.testForceExpireSoon();
    };

    // show or hide the coach banner (cs-coach-banner) without a full coach
    // setup, so E2E can check its safe-area layout. publishes the real
    // coachBannerEvent the intake form uses, which renders the same banner
    // component and toggles the same coach-banner-visible host class as
    // manage mode. pass a name to show, null to hide.
    window['testSetCoachBannerAsync'] = async (clientDisplayName: string | null) => {
      this.pubSub.publish(coachBannerEvent, clientDisplayName ? { clientDisplayName } : null);
    };

    // make the signed-in account look as if it accepted an OLDER Terms text and was
    // first asked about the current one a day ago, so the home card and the "We've
    // updated our Privacy Policy" screen come up the way they do in the gentle month
    // (#5811, #5812, #5813). IN MEMORY ONLY: it edits the cached login result and
    // writes nothing, so the next CouchDB login puts the real records back. The
    // asked-mark is set for the current version on purpose — without it the consent
    // dialog would write one to the server.
    window['testOweConsentRefreshAsync'] = async () => {
      const consents = this.auth.couch?.consents;
      const tos = consents?.[UserConsentId.ToS];
      if (!tos) throw new Error('testOweConsentRefreshAsync: the signed-in account has no Terms record yet');
      tos.textVersion = CONSENT_TEXT_VERSION - 1;
      tos.refreshAsked = { textVersion: CONSENT_TEXT_VERSION, at_utc: DateTime.utc().minus({ days: 1 }).toISO() };
      this.pubSub.publish(accountConsentsChangedEvent, undefined);
    };

    // undo testOweConsentRefreshAsync: a fresh CouchDB login puts the real consent
    // records back, and the event tells the home card to re-check them — without it
    // the card stays on the dashboard for whatever scenario runs next
    window['testRestoreConsentsAsync'] = async () => {
      if (!await this.auth.attemptCouchLoginAsync(CouchLoginReason.E2eTestHook)) {
        throw new Error('testRestoreConsentsAsync: the CouchDB login failed, so the edited consent records are still in memory');
      }
      this.pubSub.publish(accountConsentsChangedEvent, undefined);
    };

    // wake every doc watcher on the user database, the way a document arriving from
    // sync does. #5280: the intake form's own watcher callback is what rebuilds the
    // program summary, and the bug it guards against is only reachable when that
    // callback runs while the user sits on the summary. A local save does NOT wake it
    // — only a PULLED document does (database-wrapper.ts, applyPulledDocToCache) — so
    // a test has no other way to reach the callback. This calls the same method the
    // database's own recovery path uses and does nothing else: it writes no document
    // and changes no state, it only tells the watchers to re-read.
    window['testNotifyAllWatchersAsync'] = async () => {
      this.userDb.database.notifyAllWatchersChanged();
    };
    // #6540: make the curated reads of the given exercise ids fail, the way a timed-out fetch
    // does, so a spec can show what the intake form's Strength step does then. `null` turns it
    // off. The intake form reads its exercises while the page loads, so the setting is kept in
    // sessionStorage and put back on at the next boot: a spec turns it on, reloads, and the
    // page loads with the reads failing.
    window['testFailCuratedExerciseReadsAsync'] = async (exerciseIds: string[] | null) => {
      try {
        if (exerciseIds?.length) {
          sessionStorage.setItem(FAIL_CURATED_READS_KEY, JSON.stringify(exerciseIds));
        } else {
          sessionStorage.removeItem(FAIL_CURATED_READS_KEY);
        }
      } catch (err) {
        // a spec that cannot keep the setting over a reload must fail loudly, not test nothing
        throw new Error(`testFailCuratedExerciseReadsAsync could not store the setting: ${err}`);
      }
      this.applyCuratedReadFailures(exerciseIds ?? []);
    };
    this.applyCuratedReadFailures(this.readStoredCuratedReadFailures());
  }

  /** the ids a spec asked to fail before the last reload; nothing is stored unless a spec called the hook */
  private readStoredCuratedReadFailures(): string[] {
    try {
      const stored = sessionStorage.getItem(FAIL_CURATED_READS_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.filter(id => typeof id === 'string') : [];
    } catch (err) {
      // only a spec stores this setting; if it cannot be read back, no reads are made to
      // fail, and the spec that relied on them fails its own checks
      console.log('testFailCuratedExerciseReadsAsync: the stored setting could not be read', err);
      return [];
    }
  }

  /**
   * Wrap the live service's throwing curated read so the listed ids fail, or put the
   * original back when the list is empty. Only `getGlobalExerciseByIdOrThrowAsync` is
   * wrapped: the intake form's Strength step calls it, and so do the other callers that must
   * not take a failed read for a missing exercise (df-c21e11979ef2), so a listed id fails
   * there too until the list is cleared.
   */
  private applyCuratedReadFailures(exerciseIds: string[]) {
    // the wrapper is an own property of the instance, so deleting it brings back the
    // class's method unchanged
    delete (this.exerciseDb as Partial<ExerciseDbService>).getGlobalExerciseByIdOrThrowAsync;
    if (exerciseIds.length === 0) {
      return;
    }
    console.log(`E2E: curated reads of ${exerciseIds.join(', ')} will fail`);
    const exerciseDb = this.exerciseDb;
    exerciseDb.getGlobalExerciseByIdOrThrowAsync = async (id, watcher, language) => {
      if (exerciseIds.includes(id)) {
        const err = new Error(`E2E: forced read failure for ${id}`);
        err.name = 'TimeoutError';
        throw err;
      }
      return ExerciseDbService.prototype.getGlobalExerciseByIdOrThrowAsync.call(exerciseDb, id, watcher, language);
    };
  }
}

/** the sessionStorage key behind window.testFailCuratedExerciseReadsAsync */
const FAIL_CURATED_READS_KEY = 'cs-e2e-fail-curated-exercise-reads';
